/**
 * Motion of the configurator: the ink highlight glides from the old choice to the new one inside each group (a shared
 * layout id), and a value that changes (a price, a rule) slides in instead of snapping. Both drop the travel under
 * prefers-reduced-motion.
 */
import { useReducedMotion } from "@prop/ui";
import { AnimatePresence, m } from "framer-motion";
import { type ReactNode, useState } from "react";

const SPRING = { type: "spring", stiffness: 520, damping: 40, mass: 0.7 } as const;

/** The selected item's ink fill; render it inside the chosen button of a group (`group` keeps groups apart). */
export function Glide({ group, className = "rounded-[inherit]" }: { group: string; className?: string }) {
  const reduced = useReducedMotion();
  return <m.span aria-hidden layoutId={`cfg-${group}`} transition={reduced ? { duration: 0 } : SPRING} className={`absolute inset-0 z-0 bg-foreground ${className}`} />;
}

/** A value that slides up into place when it changes. */
export function Swap({ value, children, className }: { value: string; children?: ReactNode; className?: string }) {
  const reduced = useReducedMotion();
  return (
    <span className={`relative inline-flex overflow-hidden ${className ?? ""}`}>
      <AnimatePresence mode="popLayout" initial={false}>
        <m.span
          key={value}
          initial={reduced ? false : { y: "60%", opacity: 0, filter: "blur(4px)" }}
          animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
          exit={reduced ? undefined : { y: "-60%", opacity: 0, filter: "blur(4px)" }}
          transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
          className="inline-block"
        >
          {children ?? value}
        </m.span>
      </AnimatePresence>
    </span>
  );
}

/** A panel that rises into place the first time it scrolls into view (it may mount long after the page's own reveals). */
export function Rise({ children, className, delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const reduced = useReducedMotion();
  // `data-seen` starts the CSS cascade of the panel's choices (`.cfg-rise`, packages/ui).
  const [seen, setSeen] = useState(false);
  return (
    <m.div
      className={`cfg-rise ${className ?? ""}`}
      data-seen={seen || undefined}
      onViewportEnter={() => setSeen(true)}
      initial={reduced ? false : { opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -10% 0px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </m.div>
  );
}

/** Content that opens and closes with a height glide (interruptible; instant under reduced motion). */
export function Collapse({ open, children }: { open: boolean; children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <AnimatePresence initial={false}>
      {open && (
        <m.div
          key="body"
          className="overflow-hidden"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={reduced ? { duration: 0 } : { duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          {children}
        </m.div>
      )}
    </AnimatePresence>
  );
}
